import * as React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@eduviaos/shared";
import { coachApi } from "../learning/api";
import { COACH_KEY, useLearningPreferences } from "../learning/hooks";
import type { ResolvedLearningPreferences } from "../learning/types";
import type { LearnLocale } from "./learn";
import { learnStrings, readLearnLocaleOverride, writeLearnLocaleOverride } from "./learn";
import { portalShellStrings, type PortalLocale } from "./portalShell";
import { intakeWizardStrings } from "./intakeWizard";
import { studyGroupChatStrings } from "./studyGroupChat";
import { publishLearnLocale } from "./learnLocaleBridge";

function mergeStrings(locale: PortalLocale) {
  return {
    ...portalShellStrings(locale),
    ...learnStrings(locale),
    ...intakeWizardStrings(locale),
    ...studyGroupChatStrings(locale),
  };
}

export type PortalStrings = ReturnType<typeof mergeStrings>;

function patchPrefsLanguage(
  old: ResolvedLearningPreferences | undefined,
  language: PortalLocale,
): ResolvedLearningPreferences | undefined {
  if (!old) return old;
  return { ...old, content_language: language };
}

/** Blocks server hydrate from reverting a user pick while PATCH is in flight (all hook instances). */
export const languagePreferencePendingRef: { current: PortalLocale | null } = { current: null };

/** `device_only` = applied here but not saved to the account (no roster link). */
export type LanguageSyncStatus = "device_only" | "failed";

function languageSyncStatus(err: unknown): LanguageSyncStatus | null {
  if (err == null) return null;
  if (err instanceof ApiError && err.status === 403) return "device_only";
  return "failed";
}

/** SC-17 — portal-wide locale with server-synced language (SC-17c). */
export function usePortalLocale(enabled = true) {
  const qc = useQueryClient();
  const prefsQuery = useLearningPreferences(enabled);
  const serverLocale = prefsQuery.data?.content_language;
  const lastRequestedLocale = React.useRef<PortalLocale | null>(null);
  const serverHydrated = React.useRef(false);

  const [optimistic, setOptimistic] = React.useState<PortalLocale | null>(() =>
    readLearnLocaleOverride(),
  );

  const updateMut = useMutation({
    mutationFn: (next: PortalLocale) =>
      coachApi.updateLearningPreferences({ preferred_language: next }),
    onMutate: (next) => {
      lastRequestedLocale.current = next;
      languagePreferencePendingRef.current = next;
      writeLearnLocaleOverride(next);
      setOptimistic(next);
      const previous = qc.getQueryData<ResolvedLearningPreferences>(COACH_KEY.learningPreferences);
      qc.setQueryData<ResolvedLearningPreferences>(COACH_KEY.learningPreferences, (old) =>
        patchPrefsLanguage(old, next),
      );
      return { previous };
    },
    onSuccess: (data, variables) => {
      if (lastRequestedLocale.current !== variables) return;
      writeLearnLocaleOverride(data.content_language);
      qc.setQueryData(COACH_KEY.learningPreferences, data);
      if (data.content_language === variables) {
        setOptimistic(null);
        languagePreferencePendingRef.current = null;
      }
    },
    onError: (err, variables, ctx) => {
      if (lastRequestedLocale.current !== variables) return;
      languagePreferencePendingRef.current = null;

      // 403 means the learner isn't linked to a roster yet, so there is no
      // account to save the choice against — but the UI can still honour it.
      // Rolling back here snapped the whole portal from Hindi to English a
      // beat after the tap, which reads as the toggle being broken.
      if (err instanceof ApiError && err.status === 403) {
        writeLearnLocaleOverride(variables);
        setOptimistic(variables);
        return;
      }

      if (ctx?.previous) {
        qc.setQueryData(COACH_KEY.learningPreferences, ctx.previous);
        const lang = ctx.previous.content_language;
        writeLearnLocaleOverride(lang);
        setOptimistic(lang);
      } else if (serverLocale) {
        writeLearnLocaleOverride(serverLocale);
        setOptimistic(null);
      } else {
        writeLearnLocaleOverride("en");
        setOptimistic(null);
      }
    },
  });

  // One-time server hydrate — never stomp a local pick or re-run on stale refetches.
  React.useEffect(() => {
    if (!enabled || !serverLocale || !prefsQuery.isFetched) return;
    if (updateMut.isPending || languagePreferencePendingRef.current) return;
    if (serverHydrated.current) return;

    serverHydrated.current = true;
    const stored = readLearnLocaleOverride();
    if (stored !== null && stored !== serverLocale) return;

    writeLearnLocaleOverride(serverLocale);
    setOptimistic(null);
  }, [enabled, serverLocale, prefsQuery.isFetched, updateMut.isPending]);

  const locale: PortalLocale = optimistic ?? readLearnLocaleOverride() ?? serverLocale ?? "en";
  publishLearnLocale(locale);

  // Keep the document language in sync so screen readers pick the right voice
  // and `:lang()` / hyphenation rules apply. Nothing else writes this attribute.
  React.useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = React.useCallback(
    (next: LearnLocale) => {
      if (!enabled) return;
      updateMut.mutate(next);
    },
    [enabled, updateMut],
  );

  return {
    locale,
    setLocale,
    strings: mergeStrings(locale),
    prefs: prefsQuery.data,
    prefsLoading: prefsQuery.isLoading,
    languageSyncPending: updateMut.isPending,
    /*
     * A code rather than a message: the backend only speaks English, and the
     * language panel is the one place guaranteed to be read by someone who
     * may not. The view maps this onto localised copy.
     */
    languageSyncStatus: languageSyncStatus(updateMut.error),
  };
}
